import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { CourseNavLinks } from "@/components/course-nav-links";
import { ForumPanel } from "@/components/forum-panel";
import { useAuthStore } from "@/stores/authStore";
import { fetchPlayerOutline } from "@/services/api/player";

export function TenantLearnCommunityPage() {
  const { tenantSlug = "", courseSlug = "" } = useParams<{
    tenantSlug: string;
    courseSlug: string;
  }>();
  const [searchParams] = useSearchParams();
  const tNav = useTranslations("nav");
  const tForum = useTranslations("forum");
  const tPlayer = useTranslations("player");
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const outlineQuery = useQuery({
    queryKey: ["player-outline", courseSlug],
    queryFn: () => fetchPlayerOutline(courseSlug),
    enabled: isAuthenticated && Boolean(courseSlug),
    retry: false,
  });

  if (!isAuthenticated) {
    return <Navigate to={`/t/${tenantSlug}/login`} replace />;
  }

  if (outlineQuery.isLoading) {
    return (
      <main
        className="mx-auto max-w-3xl px-4 py-6 sm:px-6 lg:py-8"
        data-testid="course-community-loading"
      >
        <p className="text-sm text-[var(--text-muted)]">{tPlayer("loading")}</p>
      </main>
    );
  }

  if (outlineQuery.isError || !outlineQuery.data) {
    const status = (outlineQuery.error as { response?: { status?: number } }).response?.status;
    if (status === 403) {
      return <Navigate to={`/t/${tenantSlug}/courses/${courseSlug}`} replace />;
    }
    return (
      <main
        className="mx-auto max-w-3xl px-4 py-6 sm:px-6 lg:py-8"
        data-testid="course-community-not-found"
      >
        <p className="text-sm text-[var(--text-muted)]">
          {status === 404 ? tPlayer("courseNotFound") : tPlayer("courseLoadFailed")}
        </p>
      </main>
    );
  }

  const firstLesson = outlineQuery.data.modules.flatMap((mod) => mod.lessons)[0];
  const lessonIdParam = searchParams.get("lessonId");
  const lessonFilter = lessonIdParam && lessonIdParam !== "all" ? lessonIdParam : undefined;

  return (
    <main className="mx-auto max-w-3xl px-4 py-6 sm:px-6 lg:py-8" data-testid="course-community">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
        <Link
          to={`/t/${tenantSlug}/dashboard`}
          className="text-[var(--text-muted)] hover:underline"
          data-testid="community-back-home"
        >
          {tNav("backToDashboard")}
        </Link>
        {firstLesson ? (
          <Link
            to={`/t/${tenantSlug}/learn/${courseSlug}`}
            className="text-[var(--text-heading)] hover:underline"
            data-testid="community-back-to-player"
          >
            {outlineQuery.data.course.title}
          </Link>
        ) : null}
      </div>

      <header className="mt-4">
        <p className="text-sm text-[var(--text-muted)]">{outlineQuery.data.course.title}</p>
        <h1 className="font-serif text-2xl font-semibold text-[var(--brand-heading)]">
          {tForum("communityTitle")}
        </h1>
        <div className="mt-2">
          <CourseNavLinks tenantSlug={tenantSlug} courseSlug={courseSlug} active="community" />
        </div>
      </header>

      <div className="mt-6">
        <ForumPanel
          courseId={outlineQuery.data.course.id}
          lessonId={lessonFilter}
          variant="community"
        />
      </div>
    </main>
  );
}
